import React from "react"
import { connect } from "react-redux"  
import { Actions as authActions, FETCHING_STREAM_FROM_TOKEN_SUCCESS  } from "../../redux/auth"  
import { useNavigate } from "react-router-dom"
import {
  Button,
  TextField,
  Box,
  Typography,
  Container,
  Paper,
  Alert
} from "@mui/material"
import validation from "../../utils/validation"
import styled from "styled-components"

const LoginFormWrapper = styled.div`
  padding: 2rem;
`

const NeedAccountLink = styled.span`
  font-size: 0.8rem;
`

function LoginForm({ user, authError, isLoading, isAuthenticated, requestUserLogin }) {  
  
  const navigate = useNavigate();

  React.useEffect(() => {
    // Only redirect if we're on the login page and user becomes authenticated
    if (isAuthenticated && user && window.location.pathname === '/login') {
      // Get the first available stream, or default to room=1
      const firstStream = user.streams?.[0] || user.user?.streams?.[0];
      const roomId = firstStream?.id || '1';
      navigate(`/stream?room=${roomId}`);
    }
  }, [user, navigate, isAuthenticated])

  const [hasSubmitted, setHasSubmitted] = React.useState(false)

  const [form, setForm] = React.useState({
    email: "",
    password: ""
  })

  const [errors, setErrors] = React.useState({})
  const validateInput = (label, value) => {
    // grab validation function and run it on input if it exists
    // if it doesn't exists, just assume the input is valid
    const isValid = validation?.[label] ? validation?.[label]?.(value) : true
    // set an error if the validation function did NOT return true
    setErrors((errors) => ({ ...errors, [label]: !isValid }))
  }

  const handleInputChange = (label, value) => {
    validateInput(label, value)
    setForm((form) => ({ ...form, [label]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // validate inputs before submitting
    Object.keys(form).forEach((label) => validateInput(label, form[label]))
    // if any input hasn't been entered in, return early
    if (!Object.values(form).every((value) => Boolean(value))) {
      setErrors((errors) => ({ ...errors, form: `You must fill out all fields.` }))
      return
    }

    setHasSubmitted(true)
    const action = await requestUserLogin({ email: form.email, password: form.password })
    // reset the password form state if the login attempt is not successful
    if (action.type === FETCHING_STREAM_FROM_TOKEN_SUCCESS) 
    {
      setForm(form => ({ ...form, password: "" }));
    } else {
      setForm(form => ({ ...form, password: "" }));
    }
  }

  const getFormErrors = () => {
    const formErrors = []
    if (authError && hasSubmitted) {
      formErrors.push(`Invalid credentials. Please try again.`)
    }
    if (errors.form) {
      formErrors.push(errors.form)
    }
    return formErrors
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome to The Monitor Room.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please login below
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If you want to learn more please contact contact@themonitorroom.com
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If you have a technical issue please contact support@themonitorroom.com
          </Typography>

          {getFormErrors().length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getFormErrors()[0]}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={form.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              error={Boolean(errors.email)}
              helperText={errors.email ? "Please enter a valid email." : "Enter the email associated with your account."}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={form.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              error={Boolean(errors.password)}
              helperText={errors.password ? "Password must be at least 7 characters." : "Enter your password."}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
            <Typography variant="body2" color="text.secondary" align="center">
              Need an account? Contact your admin.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}

const mapStateToProps = (state) => ({
    authError: state.auth.error,
    isLoading: state.auth.isLoading,
    isAuthenticated: state.auth.isAuthenticated,
    user: state.auth.user
  })

  const mapDispatchToProps = (dispatch) => ({
    requestUserLogin: ({ email, password }) => dispatch(authActions.requestUserLogin({ email, password }))
  })
  export default connect(mapStateToProps, mapDispatchToProps)(LoginForm)